import { getChoresClient } from "sj-ui-toolkit/src/client";
import type { DirectoryUserShape, StoredUser } from "@/api/assignments";
import type { AvailableUsersMap } from "@/hooks/useAvailableUsers";

// ---------------------------------------------------------------------------
// Knowledge Assessment form value types
// ---------------------------------------------------------------------------

export interface AssessmentFormItem {
  content: {
    knowledgeAssessment: {
      examKey?: string;
      status?: { progress: string };
      filters: { sections: string[] };
      parameters: { allowedProgrammingLanguages: string[] };
      timeLimit?: string;
    };
  };
  dates: {
    availableAt?: Date | string;
    dueAfter?: { days: number };
    dynamic?: boolean;
  };
  notify: {
    notifyWhenAvailable: boolean;
    sendReminders: string;
  };
  strict?: Record<string, unknown>;
}

export interface AssessmentFormValues {
  id?: string;
  tenant?: string;
  listing?: {
    title?: string;
    description?: string;
  };
  items: AssessmentFormItem[];
  target: {
    availableUsers: DirectoryUserShape[];
    users: StoredUser[];
    allUsers?: Map<string, AvailableUsersMap>;
  };
  newHire?: boolean;
}

export const DEFAULT_ASSESSMENT_FORM_VALUES = (tenantName: string): AssessmentFormValues => ({
  tenant: tenantName,
  items: [
    {
      content: {
        knowledgeAssessment: {
          examKey: "the-exam",
          status: { progress: "Assigned" },
          filters: { sections: ["secure-coding"] },
          parameters: { allowedProgrammingLanguages: [] },
        },
      },
      dates: { dynamic: false },
      notify: { notifyWhenAvailable: true, sendReminders: "None" },
    },
  ],
  target: { availableUsers: [], users: [] },
  newHire: false,
});

// Raw API response type
interface RawAssessmentData {
  uuid?: string;
  title?: string;
  startDate?: string | null;
  endDate?: string | null;
  totalLearners?: number;
  completedCount?: number;
  archivedDate?: string;
  dynamic?: boolean;
  overallStatus?: {
    code?: string;
    displayName?: string;
  };
  [key: string]: unknown;
}

// Transformed type
export interface AssessmentData {
  uuid: string | undefined;
  title: string | undefined;
  endDate: Date | null;
  totalLearners: number | undefined;
  completedCount: number | undefined;
  archivedDate: string | undefined;
  startDate: Date | null;
  statusCode: string | undefined;
  statusName: string | undefined;
  dynamic?: boolean;
  [key: string]: unknown;
}

/**
 * Load active assessments
 * - Fetches assessments from the API
 * - Filters: not archived, not completed
 * - Sorts: by startDate descending (most recent first)
 * - Limits to specified number of results (default 4)
 */
export const loadActiveAssessments = async (
  tenantName: string,
  limit: number = 4
): Promise<AssessmentData[]> => {
  if (!tenantName) {
    throw new Error("No tenant selected");
  }

  const choresClient = getChoresClient();
  const { data, error } = await choresClient.getAssessments(tenantName);

  if (error) {
    throw error;
  }

  const reports: RawAssessmentData[] = data?.reports || [];

  // Transform reports to match expected format
  const transformedReports: AssessmentData[] = reports.map(
    (report): AssessmentData => ({
      uuid: report.uuid,
      title: report.title,
      startDate: report.startDate ? new Date(report.startDate) : null,
      endDate: report.endDate ? new Date(report.endDate) : null,
      totalLearners: report.totalLearners,
      completedCount: report.completedCount,
      archivedDate: report.archivedDate,
      statusCode: report.overallStatus?.code,
      statusName: report.overallStatus?.displayName,
      dynamic: report.dynamic ?? false,
    })
  );

  // Filter for active non-archived assessments which have not been completed yet,
  // sort by start date (most recent first)
  const activeAssessments = transformedReports
    .filter(report => !report.archivedDate && report.statusCode !== "CODE_COMPLETED")
    .sort((a, b) => {
      if (!a.startDate || !b.startDate) return 0;
      return b.startDate.getTime() - a.startDate.getTime();
    })
    .slice(0, limit);

  return activeAssessments;
};

/**
 * Load all assessments for a tenant (no filter, no sort, no limit).
 * Returns every assessment including archived and completed.
 */
export const loadAllAssessments = async (tenantName: string): Promise<AssessmentData[]> => {
  if (!tenantName) {
    throw new Error("No tenant selected");
  }

  const choresClient = getChoresClient();
  const { data, error } = await choresClient.getAssessments(tenantName);

  if (error) {
    throw error;
  }

  const reports: RawAssessmentData[] = data?.reports || [];

  return reports.map(
    (report): AssessmentData => ({
      uuid: report.uuid,
      title: report.title,
      startDate: report.startDate ? new Date(report.startDate) : null,
      endDate: report.endDate ? new Date(report.endDate) : null,
      totalLearners: report.totalLearners,
      completedCount: report.completedCount,
      archivedDate: report.archivedDate,
      statusCode: report.overallStatus?.code,
      statusName: report.overallStatus?.displayName,
      dynamic: report.dynamic ?? false,
    })
  );
};
